import React, { useEffect, useState } from "react";
import { Form, Select, Input, Alert, Switch } from "antd";
import Gb28181 from "../../api/Gb28181";

// Dependent device -> channel pickers for a GB28181 camera. Populated from WVP's
// registered-device list (via the backend discovery endpoint) so an operator
// picks a real device/channel instead of hand-typing 20-digit national codes.
//
// The two persisted values are the device's national code (gb28181_device_id)
// and the CHANNEL's own national code (gb28181_channel_id) — the backend surfaces
// the latter as `channelId` (WVP keeps it in a field named `deviceId`). Both are
// wired through Form.Item name=..., so function3.js's handleEditSubmit collects
// and saves them with no change to the submit handler.
//
// A "手動輸入" switch at the top flips between the picker and plain text inputs;
// the two modes share the same Form.Item names, so antd preserves the entered
// value across a toggle. A discovery failure forces the manual side so a WVP
// outage can't block camera config.
const onlineBadge = (ok) => (ok ? "🟢" : "⚪");

export default function Gb28181Fields({ form, formData }) {
  const [devices, setDevices] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loadErr, setLoadErr] = useState(false);
  const [manual, setManual] = useState(false);

  const savedDevice = formData?.gb28181_device_id || undefined;

  // Hydrate: load the device list, and if a device is already saved, its channels
  // too — WITHOUT clearing the saved channel. Clearing only happens when the user
  // re-picks a device (onDeviceChange below). A discovery failure flips to manual.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const devs = await Gb28181.ListDevices();
        if (!alive) return;
        setDevices(devs);
        if (savedDevice) {
          const chs = await Gb28181.ListChannels(savedDevice);
          if (alive) setChannels(chs);
        }
      } catch (e) {
        if (!alive) return;
        setLoadErr(true);
        setManual(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [savedDevice]);

  const onDeviceChange = async (deviceId) => {
    // A user re-pick means the previously selected channel no longer belongs to
    // this device — drop it so a stale channel can't be saved against a new box.
    form.setFieldsValue({ gb28181_channel_id: undefined });
    setChannels([]);
    if (!deviceId) return;
    try {
      const chs = await Gb28181.ListChannels(deviceId);
      setChannels(chs);
      // A standalone camera has exactly one channel; auto-pick it so the operator
      // isn't asked to make a non-choice. NVRs (many channels) still require one.
      if (chs.length === 1) {
        form.setFieldsValue({ gb28181_channel_id: chs[0].channelId });
      }
    } catch (e) {
      setLoadErr(true);
      setManual(true);
    }
  };

  return (
    <>
      <Form.Item label="28181 輸入方式">
        <Switch
          checkedChildren="手動輸入"
          unCheckedChildren="清單選擇"
          checked={manual}
          onChange={setManual}
        />
      </Form.Item>

      {loadErr && manual && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="無法取得 28181 設備清單，請手動輸入 28181 編號。"
        />
      )}

      {manual ? (
        <>
          <Form.Item
            label="28181 設備編號"
            name="gb28181_device_id"
            initialValue={formData?.gb28181_device_id}
          >
            <Input placeholder="20 碼 28181 設備編號" />
          </Form.Item>
          <Form.Item
            label="28181 通道編號"
            name="gb28181_channel_id"
            initialValue={formData?.gb28181_channel_id}
          >
            <Input placeholder="20 碼 28181 通道編號" />
          </Form.Item>
        </>
      ) : (
        <>
          <Form.Item
            label="28181 設備"
            name="gb28181_device_id"
            initialValue={formData?.gb28181_device_id}
          >
            <Select
              showSearch
              allowClear
              placeholder="選擇已註冊的 28181 設備"
              optionFilterProp="label"
              onChange={onDeviceChange}
              options={devices.map((d) => ({
                value: d.deviceId,
                label: `${onlineBadge(d.online)} ${d.name || d.deviceId}${
                  d.model ? ` — ${d.model}` : ""
                }${d.ip ? ` (${d.ip})` : ""}`,
              }))}
            />
          </Form.Item>
          <Form.Item
            label="28181 通道"
            name="gb28181_channel_id"
            initialValue={formData?.gb28181_channel_id}
          >
            <Select
              showSearch
              allowClear
              placeholder="選擇通道"
              optionFilterProp="label"
              notFoundContent="請先選擇設備"
              options={channels.map((c) => ({
                value: c.channelId,
                label: `${onlineBadge(c.status === "ON")} ${
                  c.name || c.channelId
                }`,
              }))}
            />
          </Form.Item>
        </>
      )}
    </>
  );
}
